import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, RotateCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

export class RightAngleConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 200;
    const startY = options.y ?? 450;
    const size = options.size ?? 250;
    const r = Math.min(80, size * 0.35);

    // Key points
    const pA = { x: startX, y: startY };
    const pB = { x: startX + size, y: startY };

    // Point 1: Arc 1 intersection on line AB
    const pP1 = { x: startX + r, y: startY };

    // Point 2: Arc 2 intersection on Arc 1 (60 deg, i.e., -PI/3)
    const angleP2 = -Math.PI / 3;
    const pP2 = SVGRenderer.polarToCartesian(pA, r, angleP2);

    // Point 3: Arc 3 intersection on Arc 1 (120 deg, i.e., -2*PI/3)
    const angleP3 = (-2 * Math.PI) / 3;
    const pP3 = SVGRenderer.polarToCartesian(pA, r, angleP3);

    // Point 4: Intersection of arcs from P2 and P3 directly above A
    const pP4 = { x: startX, y: startY - r * Math.sqrt(3) };
    const pTop = { x: startX, y: startY - size * 0.85 };

    // Initialize Geometry Objects
    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const baseSegment = new Segment(this.renderer, pA, pB, false);

    const arc1 = new Arc(this.renderer, pA, r, 0, -Math.PI * 0.75, true, false);
    const pointP1 = new Point(this.renderer, pP1.x, pP1.y, 'P1');

    // arc2: center P1, radius r. P2 is at -120° from P1. Sweep CCW from -90° to -150° (span 60°)
    const arc2 = new Arc(this.renderer, pP1, r, -Math.PI / 2, -5 * Math.PI / 6, true, false);
    const pointP2 = new Point(this.renderer, pP2.x, pP2.y, 'P2');

    // arc3: center P2, radius r. P3 is at 180° from P2. Sweep CW from 150° to 210° (span 60°)
    const arc3 = new Arc(this.renderer, pP2, r, 5 * Math.PI / 6, 7 * Math.PI / 6, true, true);
    const pointP3 = new Point(this.renderer, pP3.x, pP3.y, 'P3');

    // arc4: center P2, radius r. P4 is at -120° from P2. Sweep CCW from -90° to -150° (span 60°)
    const arc4 = new Arc(this.renderer, pP2, r, -Math.PI / 2, -5 * Math.PI / 6, true, false);
    // arc5: center P3, radius r. P4 is at -60° from P3. Sweep CCW from -30° to -90° (span 60°)
    const arc5 = new Arc(this.renderer, pP3, r, -Math.PI / 6, -Math.PI / 2, true, false);
    const pointP4 = new Point(this.renderer, pP4.x, pP4.y, 'P4');

    const perpLine = new Segment(this.renderer, pA, pTop, false);
    const rightAngleMark = new Angle(this.renderer, pA, pB, pTop, '90°', true);

    // Hide objects initially
    [pointA, pointB, pointP1, pointP2, pointP3, pointP4, baseSegment, perpLine, arc1, arc2, arc3, arc4, arc5, rightAngleMark].forEach((obj) =>
      obj.setVisible(false)
    );

    const steps: ConstructionStep[] = [
      {
        id: 'step1',
        title: '1. Draw Base Line AB',
        description: 'Draw horizontal line segment AB as the base arm of the angle.',
        narration: 'Step 1: Drawing baseline AB with the ruler.',
        duration: 1500,
        animations: [
          new DrawPoint(pointA),
          new DrawPoint(pointB),
          new DrawLine(baseSegment, this.ruler),
        ],
      },
      {
        id: 'step2',
        title: '2. Draw Primary Arc Centered at Vertex A',
        description: 'Draw main arc centered at A intersecting line AB.',
        narration: 'Step 2: Sweep compass to draw primary arc intersecting baseline.',
        duration: 1800,
        animations: [
          new DrawArc(arc1, this.compass),
          new DrawPoint(pointP1),
        ],
      },
      {
        id: 'step3',
        title: '3. Draw Second Arc (60° Mark P2)',
        description: 'Draw arc centered at P1 to find the 60° mark P2.',
        narration: 'Step 3: Sweep arc from P1 to construct 60 degree point P2.',
        duration: 1800,
        animations: [
          new DrawArc(arc2, this.compass),
          new DrawPoint(pointP2),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw Third Arc (120° Mark P3)',
        description: 'Move compass to P2 and draw third arc to locate P3.',
        narration: 'Step 4: Drawing third arc centered at P2 to locate P3.',
        duration: 1800,
        animations: [
          new DrawArc(arc3, this.compass),
          new DrawPoint(pointP3),
        ],
      },
      {
        id: 'step5',
        title: '5. Intersect Arcs Above Vertex',
        description: 'Draw arcs from P2 and P3 intersecting above at point P4.',
        narration: 'Step 5: Drawing intersecting arcs above to locate perpendicular point P4.',
        duration: 2000,
        animations: [
          new DrawArc(arc4, this.compass),
          new DrawArc(arc5, this.compass),
          new DrawPoint(pointP4),
        ],
      },
      {
        id: 'step6',
        title: '6. Draw Perpendicular Line',
        description: 'Using ruler, draw line from A through P4 to complete 90° angle.',
        narration: 'Step 6: Drawing vertical line from A through intersection P4.',
        duration: 1500,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(perpLine, this.ruler),
        ],
      },
      {
        id: 'step7',
        title: '7. Highlight 90° Right Angle',
        description: 'Highlight 90° right angle symbol and label.',
        narration: 'Step 7: Construction complete! Highlighting 90 degree right angle.',
        duration: 1500,
        animations: [
          new Fade(this.ruler, false),
          new DrawPoint(rightAngleMark as any),
          new Highlight(perpLine),
          new Highlight(baseSegment),
        ],
      },
    ];

    return steps;
  }
}
